import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Flame, BarChart3, Activity, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EngagementWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/dashboard/engagement-metrics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        console.error('Engagement metrics error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <Card className="border-amber-200 bg-amber-50/60" data-testid="engagement-widget-loading">
        <CardContent className="pt-6 flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const maxTrend = Math.max(...(data.trend || []).map(t => t.active_users), 1);

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50/80 to-orange-50/40" data-testid="engagement-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" data-testid="engagement-widget-title">
          <Activity className="w-5 h-5 text-amber-600" />
          Kunden-Engagement
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Top Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="engagement-widget-stats">
          <div className="p-3 rounded-xl bg-white border border-amber-200 shadow-sm" data-testid="engagement-widget-dau">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-amber-600" />
              <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">Aktiv heute</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.dau}</p>
            <p className="text-xs text-gray-500">von {data.total_portal_users} Nutzern</p>
          </div>
          <div className="p-3 rounded-xl bg-white border border-amber-200 shadow-sm" data-testid="engagement-widget-streak">
            <div className="flex items-center gap-2 mb-1">
              <Flame className="w-4 h-4 text-orange-500" />
              <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">Streak</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.avg_streak} Tage</p>
            <p className="text-xs text-gray-500">Max: {data.max_streak} Tage</p>
          </div>
          <div className="p-3 rounded-xl bg-white border border-amber-200 shadow-sm" data-testid="engagement-widget-trackers">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-emerald-600" />
              <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">Aktive Tracker</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{data.active_trackers}</p>
            <p className="text-xs text-gray-500">tracken regelmässig</p>
          </div>
          <div className="p-3 rounded-xl bg-white border border-amber-200 shadow-sm" data-testid="engagement-widget-rate">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <p className="text-xs font-medium text-amber-700 uppercase tracking-wider">Engagement</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {data.total_portal_users > 0 ? Math.round((data.dau / data.total_portal_users) * 100) : 0}%
            </p>
            <p className="text-xs text-gray-500">DAU / Total</p>
          </div>
        </div>

        {/* 7-Day Trend Mini Chart */}
        <div className="p-4 rounded-xl bg-white border border-amber-200 shadow-sm" data-testid="engagement-widget-trend">
          <p className="text-xs font-medium text-amber-700 uppercase tracking-wider mb-3">7-Tage Aktivitätstrend</p>
          <div className="flex items-end gap-1 h-16">
            {(data.trend || []).map((day, i) => {
              const height = maxTrend > 0 ? Math.max((day.active_users / maxTrend) * 100, 4) : 4;
              const isToday = i === (data.trend || []).length - 1;
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={`w-full rounded-t-md transition-all ${isToday ? 'bg-amber-500' : 'bg-amber-300'}`}
                    style={{ height: `${height}%`, minHeight: '2px' }}
                    title={`${day.date}: ${day.active_users} aktive Nutzer`}
                  />
                  <span className={`text-[10px] ${isToday ? 'font-bold text-amber-700' : 'text-gray-400'}`}>
                    {day.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Features */}
        <div className="p-4 rounded-xl bg-white border border-amber-200 shadow-sm" data-testid="engagement-widget-features">
          <p className="text-xs font-medium text-amber-700 uppercase tracking-wider mb-3">Beliebteste Features (heute)</p>
          <div className="space-y-2">
            {(data.top_features || []).map((feat, i) => {
              const maxCount = Math.max(...(data.top_features || []).map(f => f.count), 1);
              const width = Math.max((feat.count / maxCount) * 100, 2);
              return (
                <div key={feat.name} className="flex items-center gap-3" data-testid={`engagement-feature-${i}`}>
                  <span className="text-sm text-gray-700 w-28 flex-shrink-0">{feat.name}</span>
                  <div className="flex-1 h-5 bg-amber-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-500"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-gray-800 w-8 text-right">{feat.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EngagementWidget;
